import { Typography } from '@mui/material';
import { css, cx } from '@styled/css';
import type { CSSProperties } from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import './Loading.scss';

import { TimedAction } from 'helpers/timed_action';
import { prevent } from 'helpers/utils';
import { AUTOMATION_IDS } from 'state_management/automationIds';

const DEFAULT_ENTER_DELAY = 0;
const DEFAULT_EXIT_DELAY = 500;
export type EMPTY_LOADING_CONFIG_TYPE = { show: boolean; message: string; enterDelay?: number; exitDelay?: number };
export const EMPTY_LOADING_CONFIG: EMPTY_LOADING_CONFIG_TYPE = { show: false, message: '', enterDelay: DEFAULT_ENTER_DELAY };

type Props = {
  show: boolean;
  message: string;
  exitDelay?: number;
  enterDelay?: number;
  autoId?: string;
  isFixedPosition?: boolean;
  style?: CSSProperties;
};

export default function Loading({ show, message, exitDelay = DEFAULT_EXIT_DELAY, enterDelay = DEFAULT_ENTER_DELAY, autoId = 'loading-state', isFixedPosition = false, style }: Props) {
  const [showSpinner, setShowSpinner] = useState(false);

  const entryTimedAction = useRef<TimedAction | null>(null);
  const exitTimedAction = useRef<TimedAction | null>(null);
  const [enterDelayCompleted, setEnterDelayCompleted] = useState(false);
  const [exitDelayCompleted, setExitDelayCompleted] = useState(false);
  const [preservedMessage, setPreservedMessage] = useState<string>('');

  useLayoutEffect(() => {
    const enterDelayCallback = () => {
      setEnterDelayCompleted(true);

      exitTimedAction.current = new TimedAction(() => {
        setExitDelayCompleted(true);
      }, exitDelay);

      exitTimedAction.current.enqueue();
    };

    if (show) {
      setExitDelayCompleted(false);
      exitTimedAction.current = null;
      if (enterDelay) {
        entryTimedAction.current = new TimedAction(() => {
          enterDelayCallback();
        }, enterDelay);

        entryTimedAction.current.enqueue();
      } else {
        enterDelayCallback();
      }
    } else if (entryTimedAction.current || !enterDelay) {
      if (entryTimedAction.current) {
        entryTimedAction.current.cancel();
      }
      entryTimedAction.current = null;
      setEnterDelayCompleted(false);
      setPreservedMessage('');
    }

    // Cleanup function to cancel any pending timers when component unmounts
    return () => {
      if (entryTimedAction.current) {
        entryTimedAction.current.cancel();
        entryTimedAction.current = null;
      }
    };
  }, [show, enterDelay, exitDelay]);

  useLayoutEffect(() => {
    if (enterDelayCompleted || (exitTimedAction.current && !exitDelayCompleted)) {
      setShowSpinner(true);
      if (message) {
        setPreservedMessage(message);
      }
    } else {
      setShowSpinner(false);
      setPreservedMessage('');
    }
  }, [enterDelayCompleted, exitDelayCompleted, message]);

  useEffect(() => {
    return () => {
      if (exitTimedAction.current) {
        exitTimedAction.current.cancel();
        exitTimedAction.current = null;
      }
    };
  }, []);

  return showSpinner ? (
    <div
      onClick={prevent}
      auto-id={autoId}
      className={cx(`loadingState ${isFixedPosition ? 'fixedPositionLoading' : ''}`, css({ zIndex: 'max' }))}
      data-testid={AUTOMATION_IDS.LOADING.CONTAINER}
      style={style}>
      <div
        className="loadingIndicator"
        data-testid={AUTOMATION_IDS.LOADING.INDICATOR}>
        <div className="icon dotsCircularProgress"></div>
        <Typography
          fontSize={14}
          data-testid={AUTOMATION_IDS.LOADING.MESSAGE}>
          {preservedMessage?.trim() ? preservedMessage : 'Loading'}
        </Typography>
      </div>
    </div>
  ) : null;
}
